import { OlModeProps, OlModeTable } from '@repo/toolkit/ol-mode-table-helper'
import { StringFormatter } from '@repo/toolkit/string'
import { useNavigator } from '@repo/toolkit/use-navigator'
import { Column } from '@repo/ui/data-table'
import { useRef } from 'react'

import { API, constructApiUrl } from '../../../../api/apis.config'
import { Paths } from '../../../../lib/constants/path'
import { useNativeCountryName } from '../../../../lib/hooks/use-native-country-name'
import useTranslate from '../../../../lib/hooks/use-translate'
import { TeachingHall } from '../../../../lib/types/models/ol/teaching-hall/teaching-hall'
import { formatPhoneNumber } from '../../../../utils/functions/format-phone-number'
import DynamicDataTable, { DynamicTableRef } from '../../../elements/dynamic-table'
import { olNameColumn } from '../common/ol-columns'

type OlTeachingHallsListPageProps = OlModeProps

const OlTeachingHallsListPage = (props: OlTeachingHallsListPageProps) => {
  const { navigateWithEvent } = useNavigator()
  const tableRef = useRef<DynamicTableRef<TeachingHall>>(null)
  const { t } = useTranslate()
  const nativeCountryName = useNativeCountryName()

  const mode = new OlModeTable(props)

  const endpoint = mode.endpoint({
    ol: (olId) => constructApiUrl(API.ols.getTeachingHalls.url, { olId }),
    global: API.ols.getAllTeachingHalls.url
  })

  const baseColumns: Column<TeachingHall>[] = [
    {
      accessorKey: 'name',
      header: t('teaching.hall.table.name'),
      enableSorting: true
    },
    {
      accessorKey: 'address1',
      header: t('teaching.hall.table.address'),
      enableSorting: true,
      cell: ({ row }) => {
        const fullAddress = [
          row.original.address1,
          row.original.address2,
          row.original.city,
          row.original.postalCode,
          nativeCountryName(row.original.country)
        ]
          .filter(Boolean)
          .join(', ')

        return <div className="max-w-[200px] whitespace-normal break-words">{fullAddress}</div>
      }
    },
    {
      accessorKey: 'numberOfClassrooms',
      header: t('teaching.hall.table.number.of.classrooms'),
      enableSorting: true
    },
    {
      accessorKey: 'contactLastName',
      header: t('teaching.hall.table.contact'),
      enableSorting: true,
      cell: ({ row }) =>
        row.original.contactLastName.toUpperCase() +
        ' ' +
        StringFormatter.capitalize(row.original.contactFirstName)
    },
    {
      accessorKey: 'contactPhone',
      header: t('teaching.hall.table.phoneNumber'),
      cell: ({ row }) => formatPhoneNumber(row.original.contactPhone)
    }
  ]

  const columns = mode.columns(baseColumns, [olNameColumn<TeachingHall>()])

  const onRowClick = (teachingHall: TeachingHall, event?: React.MouseEvent) =>
    navigateWithEvent(
      Paths.OL_TEACHING_HALL_VIEW_PAGE.replace(':olId', teachingHall.ol.id.toString()).replace(
        ':id',
        teachingHall.id.toString()
      ),
      event
    )

  return (
    <div>
      <DynamicDataTable<TeachingHall, null>
        ref={tableRef}
        title={"Toutes les salles d'enseignement"}
        columns={columns}
        allowSearchParam
        onRowClick={onRowClick}
        permissions={{ delete: false }}
        endpoint={endpoint}
      />
    </div>
  )
}

export default OlTeachingHallsListPage
